import { useCallback, useEffect, useRef, useState } from 'react';
import { RenderListItem } from '../../api/rendersApi';
import { ApiError, isAbortError, toApiError } from '../../api/http';
import { BedtimePair } from '../../context/bedtime/bedtimeContextModel';
import { fetchApprovedForPair } from '../MyStories/approvedRenders';

export type ApprovedRendersState = 'idle' | 'loading' | 'success' | 'empty' | 'error';

export interface ApprovedRenders {
  state: ApprovedRendersState;
  renders: RenderListItem[];
  error: ApiError | null;
  reload: () => void;
}

/**
 * Read-only view of the approved shelf for the committed pair. Stale
 * responses are dropped when the pair changes. No caching side effects; My
 * stories keeps those.
 */
export function useApprovedRenders(pair: BedtimePair | null, enabled: boolean): ApprovedRenders {
  const [state, setState] = useState<ApprovedRendersState>('idle');
  const [renders, setRenders] = useState<RenderListItem[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [nonce, setNonce] = useState(0);
  const requestRef = useRef(0);
  const pairKey = pair ? `${pair.householdId}:${pair.listenerId}:${pair.voiceId}` : '';

  useEffect(() => {
    if (!pair || !enabled) {
      requestRef.current += 1;
      setState('idle');
      setRenders([]);
      setError(null);
      return undefined;
    }
    const controller = new AbortController();
    const requestId = ++requestRef.current;
    setState('loading');
    setError(null);
    fetchApprovedForPair(pair, controller.signal)
      .then((rows) => {
        if (requestId !== requestRef.current || controller.signal.aborted) return;
        setRenders(rows);
        setState(rows.length ? 'success' : 'empty');
      })
      .catch((reason) => {
        if (isAbortError(reason) || requestId !== requestRef.current || controller.signal.aborted) return;
        setError(toApiError(reason));
        setState('error');
      });
    return () => {
      controller.abort();
      requestRef.current += 1;
    };
    // pairKey stands in for the pair's identity fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairKey, enabled, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { state, renders, error, reload };
}
